/**
 * Image map — bilingual visual assets.
 *
 * Per client Decision 6 (Rapport de pré-publication v2 §9):
 *   - The client supplies FR visuals progressively
 *   - No AI generation of visuals is permitted
 *   - No automatic translation of text embedded in images
 *   - Fallback: if a FR version is not yet delivered (`fr: null`), the EN
 *     version is displayed until an approved FR file is provided
 *   - Every expected replacement is documented here with its target FR
 *     filename so the client can drop it in when ready
 *
 * Note: images that currently still display "Solu-Cortef®" are kept intact
 * (per client instruction). They are listed here with `fr: null` and a
 * `pendingClientDeliverable` flag so the future FR version cleanly slots in.
 */
export const imageMap = {
  // ---------- Hero banners ----------
  homeHero: {
    en: '/images/wqlk87v9_banner_mgp-Photoroom.jpg',
    fr: '/images/Myguardpod_home_fr.jpg',
    pendingClientDeliverable: false,
    altEn: 'Protective carrying cases for emergency medications',
    altFr: 'Étuis protecteurs pour médicaments d\u2019urgence',
  },
  storyHero: {
    en: '/images/shzngc2k_image.jpg',
    fr: '/images/MGP_Story_fr.jpg',
    pendingClientDeliverable: false,
    altEn: 'Born from a real emergency in Baie-Comeau, Québec',
    altFr: 'N\u00e9 d\u2019une vraie urgence \u00e0 Baie-Comeau, Qu\u00e9bec',
  },
  healthcareHero: {
    en: '/images/vbf2u82c_mgp_healthcare-Photoroom.jpg',
    fr: '/images/MGP_healthcare_fr.jpg',
    pendingClientDeliverable: false,
    solucortefStillVisible: false,
    altEn: 'Solutions for schools, clinics, pharmacies and emergency services',
    altFr: 'Solutions pour \u00e9coles, cliniques, pharmacies et services d\u2019urgence',
  },
  productsHero: {
    en: '/images/sb4ytv13_MGP_product-Photoroom.jpg',
    fr: '/images/MGP_Product_Fr.webp',
    pendingClientDeliverable: false,
    altEn: 'MyGuardPod \u2014 Protect What Protects You',
    altFr: 'MyGuardPod \u2014 Prot\u00e9gez ce qui vous prot\u00e8ge',
  },

  // ---------- Project ACT (Solu-Cortef still visible in these images) ----------
  projectActLineup: {
    en: '/images/sv0z24k6_lineup_project_ACT.jpg',
    fr: null, // expected FR filename: lineup_project_ACT_fr.jpg
    pendingClientDeliverable: true,
    solucortefStillVisible: true,
    altEn: 'Project ACT\u2122 lineup: CortoPod Mini, CortoPod, CortoPod Plus',
    altFr: 'Gamme Projet ACT\u2122\u00a0: CortoPod Mini, CortoPod, CortoPod Plus',
  },
  projectActLineupHero: {
    en: '/images/gvaqf2qv_lineup_project_ACT.jpg',
    fr: '/images/Project_act_fr.jpg',
    pendingClientDeliverable: false,
    solucortefStillVisible: true,
    altEn: 'Project ACT\u2122 lineup: CortoPod Mini, CortoPod, CortoPod Plus',
    altFr: 'Gamme Projet ACT\u2122\u00a0: CortoPod Mini, CortoPod, CortoPod Plus',
  },
  cortopodAct: {
    en: '/images/Project_ACt_REg.jpg',
    fr: '/images/Project_ACt_REg_fr.jpg',
    pendingClientDeliverable: false,
    solucortefStillVisible: false,
    altEn: 'CortoPod ACT\u2122 \u2014 organized emergency hydrocortisone kit format',
    altFr: 'CortoPod ACT\u2122 \u2014 format organis\u00e9 pour kit d\u2019hydrocortisone d\u2019urgence',
  },
  cortopodActMini: {
    en: '/images/p25xo7wq_Cortopod_ACT_Mini.jpg',
    fr: '/images/Project_ACt_mini_fr.jpg',
    pendingClientDeliverable: false,
    solucortefStillVisible: true,
    altEn: 'CortoPod ACT Mini \u2014 vial-only ultra-compact format',
    altFr: 'CortoPod ACT Mini \u2014 format ultra-compact fiole seule',
  },
  cortopodActPlus: {
    en: '/images/wzbylzwf_Cortopod_ACT_PLUS.jpg',
    fr: '/images/Project_ACt_plus_fr.jpg',
    pendingClientDeliverable: false,
    solucortefStillVisible: true,
    altEn: 'CortoPod ACT Plus \u2014 expanded capacity format',
    altFr: 'CortoPod ACT Plus \u2014 format \u00e0 capacit\u00e9 \u00e9largie',
  },
  cardHolder: {
    en: '/images/yhc6qglg_emergency_card_holder.jpg',
    fr: '/images/MGP_CARD_HOLDER_fr.jpg',
    pendingClientDeliverable: false,
    altEn: 'MyGuardPod emergency information card holder assembly',
    altFr: 'Assemblage du porte-carte d\u2019information d\u2019urgence MyGuardPod',
  },
  projectActRegBundle: {
    en: '/images/g58mr8b8_Project_act_reg_bundle.jpg',
    fr: '/images/bundlemini_reg_fr.jpg',
    pendingClientDeliverable: false,
    altEn: 'CortoPod ACT + Mini ACT bundle',
    altFr: 'Ensemble CortoPod ACT + Mini ACT',
  },
  projectActPlusBundle: {
    en: '/images/yimn9k3s_Project_act_plus_bundle.jpg',
    fr: '/images/bundle_reg_fr.jpg',
    pendingClientDeliverable: false,
    altEn: 'CortoPod ACT Plus + Mini ACT bundle',
    altFr: 'Ensemble CortoPod ACT Plus + Mini ACT',
  },
  projectActFullBundle: {
    en: '/images/Full_bundle_en.jpg',
    fr: '/images/full_bundle_fr.jpg',
    pendingClientDeliverable: false,
    altEn: 'Complete CortoPod ACT bundle \u2014 Mini + Standard + Plus',
    altFr: 'Ensemble complet CortoPod ACT \u2014 Mini + Standard + Plus',
  },

  // ---------- Category ribbons ----------
  ribbonEpilepsy: {
    en: '/images/l37azf4s_ChatGPT_Image_16_avr._2026_13_30_55.png',
    fr: null, // expected FR filename: ribbon_epilepsy_fr.png
    pendingClientDeliverable: true,
    altEn: 'Epilepsy category',
    altFr: 'Cat\u00e9gorie \u00c9pilepsie',
  },
  ribbonAdrenal: {
    en: '/images/pcbe2bml_ChatGPT_Image_16_avr._2026_13_33_33.png',
    fr: null, // expected FR filename: ribbon_adrenal_fr.png
    pendingClientDeliverable: true,
    altEn: 'Addison\u2019s disease category',
    altFr: 'Cat\u00e9gorie Maladie d\u2019Addison',
  },
  ribbonAllergy: {
    en: '/images/k2bpgr2g_ChatGPT_Image_16_avr._2026_13_56_35.png',
    fr: null, // expected FR filename: ribbon_allergy_fr.png
    pendingClientDeliverable: true,
    altEn: 'Anaphylaxis category',
    altFr: 'Cat\u00e9gorie Anaphylaxie',
  },
  ribbonHeart: {
    en: '/images/0irdfe9w_ChatGPT_Image_16_avr._2026_14_24_08.png',
    fr: null, // expected FR filename: ribbon_heart_fr.png
    pendingClientDeliverable: true,
    altEn: 'Heart disease category',
    altFr: 'Cat\u00e9gorie Maladies cardiaques',
  },
  ribbonOverdose: {
    en: '/images/cp9isp58_ChatGPT_Image_16_avr._2026_14_26_19.png',
    fr: null, // expected FR filename: ribbon_overdose_fr.png
    pendingClientDeliverable: true,
    altEn: 'Opioid overdose category',
    altFr: 'Cat\u00e9gorie Surdose d\u2019opio\u00efdes',
  },
  ribbonAsthma: {
    en: '/images/gn91kotm_ChatGPT_Image_16_avr._2026_14_30_36.png',
    fr: null, // expected FR filename: ribbon_asthma_fr.png (may be identical if no text)
    pendingClientDeliverable: true,
    altEn: 'Asthma category',
    altFr: 'Cat\u00e9gorie Asthme',
  },

  // ---------- Brand / logo ----------
  logo: {
    en: '/images/LOGO_MGP.webp',
    fr: '/images/LOGO_MGP.webp', // Same file — logo is bilingual by design
    pendingClientDeliverable: false,
    altEn: 'Official MyGuardPod logo',
    altFr: 'Logo officiel MyGuardPod',
  },
};

export default imageMap;
